import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { DotsSixVertical, ArrowsLeftRight } from '@phosphor-icons/react';

const BUILTIN_FIELDS = [
  { id: 'description', label: 'Description', zone: 'main' },
  { id: 'subtasks', label: 'Sub-tasks', zone: 'main' },
  { id: 'linked_issues', label: 'Linked Items', zone: 'main' },
  { id: 'type', label: 'Type', zone: 'sidebar' },
  { id: 'status', label: 'Status', zone: 'sidebar' },
  { id: 'priority', label: 'Priority', zone: 'sidebar' },
  { id: 'assignee', label: 'Assignee', zone: 'sidebar' },
  { id: 'due_date', label: 'Due Date', zone: 'sidebar' },
  { id: 'tags', label: 'Tags', zone: 'sidebar' },
  { id: 'reporter', label: 'Reporter', zone: 'sidebar' },
  { id: 'created', label: 'Created', zone: 'sidebar' },
];

const WORK_TYPES = [
  { id: 'task', label: 'Task' },
  { id: 'story', label: 'Story' },
  { id: 'bug', label: 'Bug' },
];

function getDefaultLayout(customFields, workType) {
  const main = ['description', 'subtasks', 'linked_issues'];
  const sidebar = ['type', 'status', 'priority', 'assignee', 'due_date', 'tags', 'reporter', 'created'];
  // Add applicable custom fields to sidebar by default
  (customFields || []).forEach(cf => {
    if (!cf.work_types?.length || cf.work_types.includes(workType)) {
      sidebar.push(cf.field_id);
    }
  });
  return { main, sidebar };
}

function getAllFields(customFields, workType) {
  const builtins = BUILTIN_FIELDS.map(f => ({ ...f, isCustom: false }));
  const customs = (customFields || [])
    .filter(cf => !cf.work_types?.length || cf.work_types.includes(workType))
    .map(cf => ({ id: cf.field_id, label: cf.name, zone: 'sidebar', isCustom: true, type: cf.type }));
  return [...builtins, ...customs];
}

export default function FieldLayoutEditor({ fieldLayouts, customFields, onSave }) {
  const [activeType, setActiveType] = useState('task');
  const [layouts, setLayouts] = useState(fieldLayouts || {});

  useEffect(() => { setLayouts(fieldLayouts || {}); }, [fieldLayouts]);

  const getLayout = (wt) => {
    if (layouts[wt]) return layouts[wt];
    return getDefaultLayout(customFields, wt);
  };

  const allFields = getAllFields(customFields, activeType);
  const currentLayout = getLayout(activeType);

  // Build a set of currently-valid field ids so we can drop any
  // references to custom fields that were deleted. Without this the
  // editor would render the orphaned `cf_xxxxx` id as raw text (see the
  // bug screenshot showing "cf_4ba785999587" in the MAIN CONTENT zone).
  const validIds = new Set(allFields.map(f => f.id));
  const stripOrphans = (ids) => (ids || []).filter(id => validIds.has(id));

  // Ensure all fields are in some zone
  const mainIds = stripOrphans(currentLayout.main);
  const sidebarIds = stripOrphans(currentLayout.sidebar);
  const allPlaced = [...mainIds, ...sidebarIds];
  const unplaced = allFields.filter(f => !allPlaced.includes(f.id)).map(f => f.id);
  const effectiveMain = [...mainIds];
  const effectiveSidebar = [...sidebarIds, ...unplaced];

  const getFieldLabel = (id) => {
    const f = allFields.find(x => x.id === id);
    return f?.label || id;
  };
  const isCustomField = (id) => {
    const f = allFields.find(x => x.id === id);
    return f?.isCustom;
  };

  const handleDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) return;

    const srcZone = source.droppableId;
    const dstZone = destination.droppableId;
    const srcList = srcZone === 'main' ? [...effectiveMain] : [...effectiveSidebar];
    const dstList = dstZone === 'main' ? (srcZone === 'main' ? srcList : [...effectiveMain]) : (srcZone === 'sidebar' ? srcList : [...effectiveSidebar]);

    const [moved] = srcList.splice(source.index, 1);

    if (srcZone === dstZone) {
      srcList.splice(destination.index, 0, moved);
      const newLayout = {
        ...layouts,
        [activeType]: {
          main: srcZone === 'main' ? srcList : effectiveMain,
          sidebar: srcZone === 'sidebar' ? srcList : effectiveSidebar,
        }
      };
      setLayouts(newLayout);
      onSave(newLayout);
    } else {
      dstList.splice(destination.index, 0, moved);
      const newLayout = {
        ...layouts,
        [activeType]: {
          main: srcZone === 'main' ? srcList : dstZone === 'main' ? dstList : effectiveMain,
          sidebar: srcZone === 'sidebar' ? srcList : dstZone === 'sidebar' ? dstList : effectiveSidebar,
        }
      };
      setLayouts(newLayout);
      onSave(newLayout);
    }
  };

  const FieldItem = ({ id, index }) => (
    <Draggable draggableId={id} index={index}>
      {(provided, snapshot) => (
        <div ref={provided.innerRef} {...provided.draggableProps}
          className={`flex items-center gap-2 px-3 py-2 rounded-md border bg-white text-[12px] font-medium transition-all
            ${snapshot.isDragging ? 'shadow-lg ring-2 ring-blue-400/30 scale-[1.02]' : 'border-zinc-200 hover:border-zinc-300'}`}>
          <div {...provided.dragHandleProps} className="cursor-grab active:cursor-grabbing text-zinc-400 hover:text-zinc-600">
            <DotsSixVertical size={14} />
          </div>
          <span className="text-zinc-800 flex-1">{getFieldLabel(id)}</span>
          {isCustomField(id) && <Badge className="text-[8px] bg-blue-50 text-blue-600 border-0">Custom</Badge>}
        </div>
      )}
    </Draggable>
  );

  const DropZone = ({ droppableId, label, items, icon }) => (
    <div className="flex-1">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[10px] uppercase tracking-[0.12em] text-zinc-500 font-bold">{label}</span>
        <Badge className="text-[9px] bg-zinc-100 text-zinc-500 border border-zinc-200">{items.length}</Badge>
      </div>
      <Droppable droppableId={droppableId}>
        {(provided, snapshot) => (
          <div ref={provided.innerRef} {...provided.droppableProps}
            className={`min-h-[120px] space-y-1.5 rounded-lg border-2 border-dashed p-2 transition-colors
              ${snapshot.isDraggingOver ? 'border-blue-300 bg-blue-50/50' : 'border-zinc-200 bg-zinc-50/50'}`}>
            {items.map((id, idx) => <FieldItem key={id} id={id} index={idx} />)}
            {provided.placeholder}
            {items.length === 0 && <p className="text-[11px] text-zinc-400 text-center py-4">Drag fields here</p>}
          </div>
        )}
      </Droppable>
    </div>
  );

  return (
    <div data-testid="field-layout-editor">
      <Tabs value={activeType} onValueChange={setActiveType}>
        <TabsList className="h-9 bg-zinc-100 mb-4">
          {WORK_TYPES.map(wt => (
            <TabsTrigger key={wt.id} value={wt.id} className="text-[12px] font-semibold">{wt.label}</TabsTrigger>
          ))}
        </TabsList>

        {WORK_TYPES.map(wt => (
          <TabsContent key={wt.id} value={wt.id}>
            <p className="text-[11px] text-zinc-500 mb-3">Drag fields between <strong>Main Content</strong> (left area with description/comments) and <strong>Side Panel</strong> (right details panel). Reorder within each zone.</p>
            <DragDropContext onDragEnd={handleDragEnd}>
              <div className="flex gap-4">
                <DropZone droppableId="main" label="Main Content (Left)" items={effectiveMain} />
                <div className="flex items-center"><ArrowsLeftRight size={16} className="text-zinc-300" /></div>
                <DropZone droppableId="sidebar" label="Side Panel (Right)" items={effectiveSidebar} />
              </div>
            </DragDropContext>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

export { getDefaultLayout, getAllFields, BUILTIN_FIELDS };
